import { useModoConsultaEvaluacion } from '../../../../hooks/useModoConsultaEvaluacion';
import React, { useContext, useState } from 'react';
import { Button, Card, Container, Form } from 'react-bootstrap';
import { addDoc, collection } from 'firebase/firestore';
import Swal from 'sweetalert2';

import { AppContext } from '../../../../Context';
import { db } from '../../../../firebase/firebase';

const textoSintoismo = `SINTOÍSMO

La palabra cinto, “camino a los dioses”, da nombre a la religión nacional de Japón, el sintoísmo, conjunto de prácticas y creencias que se expresa en las peculiares manifestaciones sociales y actitudes individuales de los habitantes del archipiélago.

El sintoísmo es fundamentalmente una religión de tipo animista. Reconoce un poder sagrado, el kami, que se encuentra en la naturaleza bajo diversas formas: el Sol (Amaterasu), la tempestad (Suzano), la Luna (Tsukiyomi) y otras muchas. Los espíritus de los antepasados también son considerados como dioses protectores de la familia o del país. No existe en el sintoísmo una profesión de fe ni unas escrituras sagradas; sus ideas sobre el pecado, la salvación o el más allá son muy imprecisas.

Su preocupación es fundamentalmente ritualista y se expresa en numerosas purificaciones y ofrendas. Poco se conoce de las prácticas religiosas de los antiguos pueblos esparcidos por las montañas de Japón. Probablemente se trataba de rituales agrícolas propios de cada estación o relacionados con determinados enclaves sagrados. Los espíritus de los lugares más venerados fueron progresivamente asociados a los clanes dominantes de tales regiones. En el siglo IV antes de la era cristiana, Jimmu, jefe de uno de los clanes, logró conquistar el país y realzó su prestigio presentándose como descendiente directo de la diosa-sol Amaterasu.

Con la centralización del país en la ciudad Yamamoto, las creencias y festividades sintoístas se convirtieron en actos ordinarios de gobierno, denominados matsuri-goto; este término llegó luego a significar toda la actividad del gobierno.

A partir del siglo VI de la era cristiana, los contactos entre el sintoísmo y el budismo modificaron profundamente ambas religiones. Los budistas adoptaron divinidades sintoístas y los sintoístas aprendieron a elegir imágenes y templos para sus dioses. Surgió una tendencia sincretista denominada Riobu Cinto. En el siglo XVII apareció un movimiento nacionalista que dio origen al sintoísmo estatal (Kokkai Shinto). Con la instauración de los Meiji, en 1868, fue proclamado religión oficial. Este sintoísmo nacionalista se prolongó hasta el final de la Segunda Guerra Mundial, cuando el emperador debió deshacer el mito de su divinidad.

La falta de una doctrina y de una estructura propia ha expuesto al sintoísmo a continuas influencias y a la formación de diversas corrientes internas. Las imágenes al borde de los caminos, los templos con sus típicos torii, y las músicas y danzas sagradas son manifestaciones de religiosidad y símbolos culturales de identificación nacional.`;

const M15_Ejercicio6 = ({ cerrarModal }) => {
    const { currentUser } = useContext(AppContext);
    const modoConsulta = useModoConsultaEvaluacion();
    const [step, setStep] = useState(1);
    const [enviando, setEnviando] = useState(false);
    const [respuestas, setRespuestas] = useState({
        estructuraRecordada: [''],
        esquemaFinal: ''
    });

    const actualizarPunto = (index, valor) => {
        setRespuestas(prev => {
            const estructuraRecordada = [...prev.estructuraRecordada];
            estructuraRecordada[index] = valor;
            return { ...prev, estructuraRecordada };
        });
    };

    const agregarPunto = () => setRespuestas(prev => ({
        ...prev,
        estructuraRecordada: [...prev.estructuraRecordada, '']
    }));

    const avanzarAVerificacion = () => {
        if (!modoConsulta && (respuestas.estructuraRecordada.every(punto => !punto.trim()))) {
            Swal.fire({
                icon: 'warning',
                title: 'Estructura incompleta',
                text: 'Escribe al menos un punto que recuerdes antes de volver a consultar la lectura.'
            });
            return;
        }
        setStep(3);
    };

    const enviarResultados = async () => {
        if (modoConsulta) { cerrarModal?.(); return; }

        if (!respuestas.esquemaFinal.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Verificación incompleta',
                text: 'Completa el esquema final después de comparar tu recuerdo con la lectura.'
            });
            return;
        }

        setEnviando(true);
        try {
            await addDoc(collection(db, 'evaluaciones'), {
                userId: currentUser.uid,
                nombre: 'Ejercicio 6 - Práctica (Sintoísmo)',
                nivel: 'Avanzado',
                modulo: '15',
                createdAt: new Date(),
                tipo: 'Lectura, reconstrucción y verificación',
                respuestas
            });
            await Swal.fire({
                title: '¡Ejercicio completado!',
                text: 'La reconstrucción y el esquema final se guardaron correctamente.',
                icon: 'success',
                confirmButtonColor: '#198754',
                confirmButtonText: 'Continuar'
            });
            if (cerrarModal) cerrarModal();
        } catch (error) {
            console.error('Error al guardar el ejercicio:', error);
            Swal.fire({ icon: 'error', title: 'No se pudo guardar', text: 'Intenta enviar nuevamente el ejercicio.' });
        } finally {
            setEnviando(false);
        }
    };

    return (
        <Container>
            <h3 className="text-center mb-4">Ejercicio 6 - Práctica</h3>

            {step === 1 && (
                <div>
                    <Card className="mb-4 shadow-sm">
                        <Card.Body>
                            <Card.Title>1. Lectura por salteo</Card.Title>
                            <Card.Text>
                                Lee rápidamente para reconocer el esquema de organización. Al avanzar, el texto se ocultará
                                para que escribas de memoria lo que recuerdas.
                            </Card.Text>
                            <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify' }}>
                                {textoSintoismo}
                            </div>
                        </Card.Body>
                    </Card>
                    <div className="d-flex justify-content-end">
                        <Button onClick={() => setStep(2)}>Ocultar lectura y continuar</Button>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div>
                    <Card className="mb-4 shadow-sm border-warning">
                        <Card.Body>
                            <Card.Title>2. Reconstrucción de memoria</Card.Title>
                            <Card.Text>Sin regresar a la lectura, escribe los elementos del esquema que recuerdes.</Card.Text>
                        </Card.Body>
                    </Card>
                    <Form>
                        {respuestas.estructuraRecordada.map((punto, index) => (
                            <Form.Group className="mb-3" key={index}>
                                <Form.Label>{`Punto ${index + 1}`}</Form.Label>
                                <Form.Control
                                    name={`estructuraRecordada${index + 1}`}
                                    value={punto}
                                    onChange={event => actualizarPunto(index, event.target.value)}
                                />
                            </Form.Group>
                        ))}
                        <Button variant="outline-secondary" onClick={agregarPunto} className="mb-4">
                            + Agregar punto
                        </Button>
                    </Form>
                    <div className="d-flex justify-content-between">
                        <Button variant="secondary" onClick={() => setStep(1)}>Atrás</Button>
                        <Button onClick={avanzarAVerificacion}>Comparar con la lectura</Button>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div>
                    <Card className="mb-4 shadow-sm border-success">
                        <Card.Body>
                            <Card.Title>3. Lectura y esquema final</Card.Title>
                            <Card.Text>
                                Lee nuevamente con tu técnica de preferencia. Compara el texto con lo que recordaste y
                                completa el esquema definitivo.
                            </Card.Text>
                            <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap', textAlign: 'justify' }}>
                                {textoSintoismo}
                            </div>
                        </Card.Body>
                    </Card>
                    <Form.Group className="mb-4">
                        <Form.Label>Esquema final de organización</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={7}
                            name="esquemaFinal"
                            value={respuestas.esquemaFinal}
                            onChange={event => setRespuestas(prev => ({ ...prev, esquemaFinal: event.target.value }))}
                        />
                    </Form.Group>
                    <div className="d-flex justify-content-between">
                        <Button variant="secondary" onClick={() => setStep(2)}>Atrás</Button>
                        <Button variant="success" disabled={enviando} onClick={enviarResultados}>
                            {enviando ? 'Guardando…' : 'Finalizar y enviar'}
                        </Button>
                    </div>
                </div>
            )}
        </Container>
    );
};

export default M15_Ejercicio6;
